import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { setAuth as persistAuth, clearAuth, getAuth, setDeviceToken, clearDeviceToken } from './api';
// Phase 115 — coach promo pop-ups are dismissed per session; an explicit
// login/signup/logout resets that so the pop-up shows again next time.
import { clearAnnouncementDismissals } from '@/lib/announcements';
import { readGuideTouch, clearGuideTouch } from '@/lib/guideAttribution';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const initial = getAuth();
    // HOTFIX (post-Phase-44.12) — DO NOT clear the cached user just because
    // there's no `sd_token` in localStorage. After the cookie migration,
    // the canonical session lives in the httpOnly `sd_session` cookie
    // (invisible to JavaScript). `sd_user` is the UI-render cache and
    // exists independently of any JS-readable token. The previous
    // "defense in depth" assumed token+user must coexist — that
    // assumption is now FALSE and was causing every cookie-mode user
    // (i.e. every user who logged in post-Phase-44.12) to be silently
    // logged out on every page reload.
    //
    // We still need to handle the original "Janelle Guardian loop" bug
    // it was protecting against (user clears only sd_token, leaving
    // stale sd_user). The /auth/me call in the useEffect below now
    // serves that role: if the cookie is also missing, /auth/me 401s
    // and refresh() clears the cached user. That's the correct gate.
    const [user, setUser] = useState(initial.user);
    const [token, setToken] = useState(initial.token);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        // Phase 44.12 — no longer gate on a localStorage token. The
        // httpOnly cookie is auto-attached by axios (withCredentials), and
        // the LEGACY bearer-token-from-localStorage path still works as
        // backup. If neither is valid, /auth/me 401s and we clear state.
        try {
            const r = await api.get('/auth/me');
            // Bolt the legal-acceptance flags onto the user so any
            // component (incl. the LegalAcceptanceModal) can react to them
            // without needing a second context.
            const merged = {
                ...r.data.user,
                instructor_profile: r.data.instructor_profile,
                parent_profile: r.data.parent_profile,
                // Phase 120 — billing dunning ladder view (null unless enforced).
                dunning: r.data.dunning || null,
                needs_legal_acceptance: !!r.data.needs_legal_acceptance,
                legal_version: r.data.legal_version,
            };
            // Phase 44.12.2 HOTFIX — keep the existing localStorage token
            // intact when refreshing. We pass the CURRENT token (not null)
            // so setAuth doesn't clobber it; for cookie-only users `token`
            // is null and that's fine (setAuth no-ops the token write).
            const { token: existingToken } = getAuth();
            persistAuth(existingToken, merged);
            setUser(merged);
            // Phase 63 hotfix — silently sync the coach's browser timezone
            // to their profile on FIRST login (only when their profile
            // tz is unset). This makes the coach's true local tz the
            // single source of truth across Schedule + Class slots
            // without ever asking them to pick a zone. Coaches who have
            // already picked one in Settings are not overridden.
            try {
                const role = merged?.role;
                const profTz = (merged?.instructor_profile?.time_zone || '').trim();
                if (role === 'instructor' && !profTz) {
                    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    if (browserTz) {
                        // Fire-and-forget — never block the login UI on this.
                        api.post('/instructor/profile/timezone-sync', { browser_timezone: browserTz })
                            .then((res) => {
                                if (res?.data?.changed) {
                                    // Patch the cached user so the rest of the
                                    // app sees the new tz immediately without
                                    // having to re-fetch /auth/me.
                                    setUser((u) => (u ? {
                                        ...u,
                                        instructor_profile: { ...(u.instructor_profile || {}), time_zone: res.data.time_zone },
                                    } : u));
                                }
                            })
                            .catch(() => { /* ignore — best-effort */ });
                    }
                }
            } catch (e) { /* Intl unavailable — ignore */ }
            return merged;
        } catch (e) {
            clearAuth();
            setUser(null);
            setToken(null);
            return null;
        }
    }, []);

    useEffect(() => {
        // Phase 44.12 — always try /auth/me on mount: cookie users have no
        // local token but ARE authenticated by the httpOnly cookie. Only
        // skip when we ALREADY have a cached user object (initial render
        // pulled it from localStorage). The 401 path inside refresh() will
        // clear stale caches cleanly.
        if (!user) refresh();
    }, []); // eslint-disable-line

    /**
     * Password login.
     * Resolves with:
     *   - `{ challenge: true, mfa_token, methods, email_masked, remember_me }`
     *     when the user has 2FA enabled and didn't present a trusted-device token.
     *   - the freshly authenticated user otherwise.
     */
    const login = useCallback(async (email, password, remember_me = false) => {
        setLoading(true);
        try {
            const r = await api.post('/auth/login', { email, password, remember_me });
            if (r.data?.requires_2fa) {
                // The interceptor already attached any cached device_token; if
                // the server still needs a second factor, surface the challenge
                // for the Login page to navigate into /login/2fa.
                return {
                    challenge: true,
                    mfa_token: r.data.mfa_token,
                    methods: r.data.methods || [],
                    email_masked: r.data.email_masked || '',
                    remember_me,
                };
            }
            persistAuth(r.data.token, r.data.user);
            clearAnnouncementDismissals();
            setToken(r.data.token);
            setUser(r.data.user);
            await refresh();
            return r.data.user;
        } finally { setLoading(false); }
    }, [refresh]);

    /**
     * Complete a 2FA challenge issued by `login`. On success persists the
     * session token (and optional device_token if remember_device was true)
     * and returns the user object.
     */
    const verify2FA = useCallback(async ({ mfa_token, method, code, remember_device = false, remember_me = false, device_label = '' }) => {
        setLoading(true);
        try {
            const r = await api.post('/auth/2fa/verify', {
                mfa_token, method, code, remember_device, remember_me, device_label,
            });
            persistAuth(r.data.token, r.data.user);
            clearAnnouncementDismissals();
            setToken(r.data.token);
            setUser(r.data.user);
            if (r.data.device_token) {
                setDeviceToken(r.data.device_token, r.data.device_token_expires_at);
            }
            await refresh();
            return r.data.user;
        } finally { setLoading(false); }
    }, [refresh]);

    const signup = useCallback(async (data) => {
        setLoading(true);
        try {
            // Phase 131b — attach first-touch guide attribution (if any).
            const touch = readGuideTouch();
            const r = await api.post('/auth/signup', touch ? { ...touch, ...data } : data);
            if (touch) clearGuideTouch();
            persistAuth(r.data.token, r.data.user);
            clearAnnouncementDismissals();
            setToken(r.data.token);
            setUser(r.data.user);
            await refresh();
            return r.data.user;
        } finally { setLoading(false); }
    }, [refresh]);

    const logout = useCallback(async () => {
        // Phase 44.12 — tell the backend to expire the httpOnly cookie so
        // we don't leave a valid session on the wire. Best-effort: even if
        // the request fails (e.g. offline), we still clear local state.
        try { await api.post('/auth/logout'); } catch { /* ignore — local clear below is what matters */ }
        clearAuth();
        clearAnnouncementDismissals();
        // Phase 118 — purge the service-worker runtime caches (they hold
        // /api/auth/me + dashboard JSON) so the next person on a shared
        // device can't read this user's data out of Cache Storage.
        try {
            if (typeof caches !== 'undefined' && caches?.keys) {
                const keys = await caches.keys();
                await Promise.all(keys.filter((k) => /api|runtime/i.test(k)).map((k) => caches.delete(k)));
            }
        } catch { /* best-effort */ }
        // NOTE: we intentionally KEEP the trusted-device token on logout so
        // the user can log back in without re-doing 2FA. Revoke it from
        // Settings → Trusted devices to clear it (or it auto-expires at 30d).
        setToken(null);
        setUser(null);
    }, []);

    // Replace the active auth (used for admin → instructor impersonation)
    const setAuth = useCallback((newToken, newUser) => {
        persistAuth(newToken, newUser);
        setToken(newToken);
        setUser(newUser);
    }, []);

    return (
        <AuthCtx.Provider value={{ user, token, loading, login, verify2FA, signup, logout, refresh, setUser, setAuth, clearDeviceToken }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    return useContext(AuthCtx);
}
